// Push notification helpers for Kinsy.
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SW_URL = '/sw-push.js';

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function subToJSON(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: {
      auth: json.keys.auth,
      p256dh: json.keys.p256dh,
    },
  };
}

export async function ensureServiceWorker() {
  if (!isPushSupported()) return null;
  // Register or fetch our SW (kept separate from any other workers)
  const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function getSubscriptionState() {
  if (!isPushSupported()) return { supported: false, permission: 'default', subscribed: false };
  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return { supported: true, permission, subscribed: !!sub };
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was denied.');
  }

  const reg = await ensureServiceWorker();
  if (!reg) throw new Error('Service worker registration failed.');

  // Fetch VAPID public key
  const { data } = await axios.get(`${API}/push/public-key`);
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  await axios.post(`${API}/push/subscribe`, subToJSON(sub), { withCredentials: true });
  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }, { withCredentials: true });
    } catch (_) {}
    await sub.unsubscribe();
  }
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`, {}, { withCredentials: true });
}
